import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { InsuranceClaim, InsuranceClaimStatusLookup } from '../../../models/insurance-claim-response.model';
import { InsuranceClaimsService } from '../../../services/insurance-claims.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';

@Component({
  selector: 'app-update-claim-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgbDropdownModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './update-claim-modal.component.html',
  styleUrl: './update-claim-modal.component.scss'
})
export class UpdateClaimModalComponent implements OnInit, OnDestroy {
  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private fb: FormBuilder = inject(FormBuilder);
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private claimsService: InsuranceClaimsService = inject(InsuranceClaimsService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private cancelSubject: Subject<void> = new Subject<void>();

  @Input() claim: InsuranceClaim | null = null;

  updateForm: FormGroup;

  statuses: InsuranceClaimStatusLookup[] = [];
  selectedStatus: InsuranceClaimStatusLookup | null = null;
  isSubmitting: boolean = false;

  constructor() {
    this.updateForm = this.fb.group({
      settledAmount: ['']
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.claim) {
      this.updateForm.patchValue({
        settledAmount: this.claim.settledAmount
      });
    }

    await this.loadStatuses();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  get statusLabel(): string {
    return this.selectedStatus?.name || this.transloco.translate('accidents.modals.updateClaim.selectStatus');
  }

  onStatusSelect(status: InsuranceClaimStatusLookup): void {
    this.selectedStatus = status;
  }

  get settledAmount(): number | null | undefined {
    const value = this.updateForm.get('settledAmount')?.value;

    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }

    const amount = Number(String(value).replace(/[^\d.-]/g, ''));
    return isNaN(amount) || amount < 0 ? undefined : amount;
  }

  get isFormValid(): boolean {
    return !!this.claim && !!this.selectedStatus && this.settledAmount !== undefined;
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  async save(): Promise<void> {
    if (!this.isFormValid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    try {
      await this.claimsService.updateInsuranceClaim({
        claimId: this.claim!.claimId,
        assetId: this.claim!.assetId,
        categoryId: this.claim!.categoryId,
        insuranceName: this.claim!.insurance,
        settledAmount: this.settledAmount as number | null,
        statusId: this.selectedStatus!.id
      }, this.cancelSubject);

      this.activeModal.close(true);
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isSubmitting = false;
        }
      });
    }
  }

  private async loadStatuses(): Promise<void> {
    try {
      const response = await this.claimsService.getInsuranceClaimStatuses(this.cancelSubject);
      this.statuses = response.data ?? [];
      this.selectedStatus = this.statuses.find(status =>
        status.id === this.claim?.statusId || status.name === this.claim?.status
      ) ?? null;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.statuses = [];
        }
      });
    }
  }
}